import { TaskReviewBalancePaymentStepper } from "components/chat/ReviewAndBalancePayment/TaskReviewBalancePaymentStepper";
import { Button } from "components/DesignSystem/Button/Button";
import { DropFile } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { ReviewLink } from "types/Models/reviewDocumentAndBalancingPayment";
import { Cross } from "components/icons/Cross";
import { LinkIcon } from "components/icons/LinkIcon";
import ToolTip from "components/design/toolTip";
import { FormikForm } from "components/FormikForm/FormikForm";
import InputFormField from "components/DesignSystem/Input/InputFormField";
import * as Yup from "yup";
import { FormikHelpers } from "formik";

type UploadFileProps = {
  showModal: boolean;
  closeModal: () => void;
  onFileSelect: (file: FileType[]) => void;
  prevSelectedFiles: FileType[];
  showStepper?: boolean;
  title?: string;
  isLoading?: boolean;
  allowSingleUpload?: boolean;
  onLinksChange?: (links: ReviewLink[]) => void;
  prevSelectedLinks?: ReviewLink[];
};

type LinkFormValues = {
  url: string;
  name: string;
};

const linkSchema = Yup.object({
  url: Yup.string()
    .required("Please enter a URL")
    .url("Please enter a valid URL"),
  name: Yup.string(),
});

const LINK_INITIAL_VALUES: LinkFormValues = { url: "", name: "" };

export const UploadFile = ({
  showModal,
  closeModal,
  onFileSelect,
  prevSelectedFiles,
  showStepper,
  title,
  isLoading,
  allowSingleUpload,
  onLinksChange,
  prevSelectedLinks = [],
}: UploadFileProps) => {
  const [selectedFiles, setSelectedFiles] =
    useState<FileType[]>(prevSelectedFiles);
  const [links, setLinks] = useState<ReviewLink[]>(prevSelectedLinks);

  const activeChannelGroupId = useSelector(
    (state: RootState) => state.reviewAndBalancePayment.activeChannelGroupId
  );
  const { uuid: groupUuid } = useCurrentGroupContext();
  const groupId = groupUuid || activeChannelGroupId;

  const removeDuplicateFile = (arr: FileType[]) => {
    const seen = new Set();
    return arr.filter((item: FileType) => {
      let identifier;
      if ("uuid" in item) {
        identifier = item.uuid;
      }
      if ("path" in item) {
        identifier = item.path;
      }

      if (!seen.has(identifier)) {
        seen.add(identifier);
        return true;
      }

      return false;
    });
  };

  const onDrop = (newFiles: FileType[]) => {
    setSelectedFiles((prev: FileType[]) =>
      removeDuplicateFile([...prev, ...newFiles])
    );
  };

  const onDelete = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number
  ) => {
    e.stopPropagation();
    setSelectedFiles((prevSelectedFiles: FileType[]) => {
      const updatedFiles = [...prevSelectedFiles];
      updatedFiles.splice(index, 1);
      return updatedFiles;
    });
  };

  const closeUploadFileModal = () => {
    setSelectedFiles([]);
    setLinks([]);
    closeModal();
  };

  const addLink = (
    values: LinkFormValues,
    formikHelpers?: FormikHelpers<LinkFormValues>
  ) => {
    const { resetForm, setFieldError } = formikHelpers!;
    if (links.some((l) => l.url === values.url)) {
      setFieldError("url", "This link has already been added");
      return;
    }

    const defaultName = values.url.replace(/^https?:\/\//, "").split("/")[0];
    setLinks((prev) => [
      ...prev,
      { url: values.url, name: values.name.trim() || defaultName },
    ]);
    resetForm();
  };

  const removeLink = (url: string) => {
    setLinks((prev) => prev.filter((l) => l.url !== url));
  };

  const saveFileForUpload = () => {
    onFileSelect(selectedFiles);
    onLinksChange?.(links);
    setSelectedFiles([]);
    setLinks([]);
  };

  const hasContent = selectedFiles.length > 0 || links.length > 0;

  return (
    <Modal.Root open={showModal} onOpenChange={closeModal}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>{title || "Update Filing"}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          {showStepper && (
            <div className="t-pb-8">
              <TaskReviewBalancePaymentStepper disable2ndStep={!hasContent} />
            </div>
          )}
          <FileInput
            groupId={groupId}
            multiple
            withInkleDocs
            files={selectedFiles as DropFile[]}
            onDelete={onDelete}
            onDrop={onDrop}
            allowSingleUpload={allowSingleUpload}
            accept={{
              "image/*": [".png", ".jpeg", ".jpg", ".webp", ".avif"],
              "application/pdf": [".pdf"],
              "application/vnd.ms-excel": [".xls"],
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                [".xlsx"],
              "text/csv": [".csv"],
            }}
          />

          {onLinksChange !== undefined && (
            <div className="t-mt-4">
              <p className="t-text-body-sm t-text-text-60 t-mb-1.5">Add link</p>
              <FormikForm
                initialValues={LINK_INITIAL_VALUES}
                onSubmit={addLink}
                validationSchema={linkSchema}
              >
                <div className="t-flex t-flex-col t-gap-2">
                  <InputFormField
                    name="name"
                    placeholder="Preview text (optional)"
                  />
                  <InputFormField
                    name="url"
                    placeholder="Paste a link (e.g. Google Drive URL)"
                    leftComponent={<LinkIcon />}
                    showLeftSeparator={false}
                  />
                  <div className="t-flex t-justify-end">
                    <Button type="submit">Add link</Button>
                  </div>
                </div>
              </FormikForm>
              {links.length > 0 && (
                <div className="t-flex t-flex-col t-gap-1.5 t-mt-3">
                  {links.map((link) => (
                    <div
                      key={link.url}
                      className="t-flex t-items-center t-gap-2 t-rounded t-border t-border-solid t-border-purple-20 t-px-3 t-py-2"
                    >
                      <span className="t-text-purple-50 t-flex-shrink-0">
                        <LinkIcon />
                      </span>
                      <div className="t-flex-1 t-min-w-0 t-flex t-flex-col">
                        <span className="t-text-body-sm t-truncate t-text-text-60">
                          {link.name}
                        </span>
                        <ToolTip text={link.url}>
                          <span className="t-text-subtext-sm t-truncate t-text-text-30">
                            {link.url}
                          </span>
                        </ToolTip>
                      </div>
                      <Button
                        type="button"
                        customType="ghost_icon"
                        size="xxsmall"
                        onClick={() => removeLink(link.url)}
                      >
                        <Cross />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="t-flex t-justify-end t-gap-3">
            <Button type="button" onClick={closeUploadFileModal}>
              Cancel
            </Button>
            <Button
              customType="primary"
              onClick={saveFileForUpload}
              disabled={!hasContent}
              isLoading={isLoading}
            >
              Next
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
